/**
 * KiekkoKingi section controller: home-shop-paths
 * Uses the theme's existing Shopify predictive-search endpoint and response section.
 */
(() => {
  const kkScript = document.currentScript;
  if (!kkScript) return;

  const rootId = kkScript.dataset.rootId;
  const sectionId = kkScript.dataset.sectionId;

  const destroy = (root) => {
    if (!root || !root.kkHomeShopPathsCleanup) return;
    root.kkHomeShopPathsCleanup.forEach((cleanup) => cleanup());
    root.kkHomeShopPathsCleanup = null;
    root.removeAttribute('data-kk-home-shop-paths-initialized');
  };

  const init = (root) => {
    if (!root || root.dataset.kkHomeShopPathsInitialized === 'true') return;
    root.dataset.kkHomeShopPathsInitialized = 'true';

    const cleanup = [];
    const search = root.querySelector('[data-kk-home-search]');
    root.kkHomeShopPathsCleanup = cleanup;

    if (!search) return;

    const form = search.querySelector('[data-kk-home-search-form]');
    const input = search.querySelector('[data-kk-home-search-input]');
    const inputWrap = input ? input.closest('.kk-home-shop-paths__input-wrap') : null;
    const placeholderTrack = search.querySelector('[data-kk-home-search-placeholder]');
    const placeholderText = search.querySelector('[data-kk-home-search-placeholder-text]');
    const clearButton = search.querySelector('[data-kk-home-search-clear]');
    const results = search.querySelector('[data-kk-home-search-results]');
    const status = search.querySelector('[data-kk-home-search-status]');
    const predictiveEnabled = search.dataset.predictiveEnabled === 'true';
    const predictiveUrl = search.dataset.predictiveUrl || '';
    const showViewAll = search.dataset.showViewAll === 'true';
    const loadingText = search.dataset.loadingText || '';
    const errorText = search.dataset.errorText || '';
    let debounceTimer = 0;
    let requestController = null;
    let countRequestController = null;
    let requestNumber = 0;
    let composing = false;
    let resultsLayoutFrame = 0;
    let mobileAlignmentFrame = 0;
    let mobileAlignmentTimer = 0;
    let placeholderFrame = 0;

    const listen = (target, eventName, handler, options) => {
      if (!target) return;
      target.addEventListener(eventName, handler, options);
      cleanup.push(() => target.removeEventListener(eventName, handler, options));
    };

    const setStatus = (message) => {
      if (status) status.textContent = message;
    };

    const setClearVisibility = () => {
      if (!clearButton || !input) return;
      clearButton.hidden = input.value.length === 0;
    };

    const updateResultsViewportHeight = () => {
      resultsLayoutFrame = 0;
      if (!results || results.hidden) return;

      const viewport = window.visualViewport;
      const viewportHeight = viewport ? viewport.height : window.innerHeight;
      const viewportTop = viewport ? viewport.offsetTop : 0;
      const resultsTop = results.getBoundingClientRect().top;
      const availableHeight = Math.floor(viewportTop + viewportHeight - resultsTop - 32);

      results.style.setProperty(
        '--kk-hsp-predictive-available-height',
        `${Math.max(180, availableHeight)}px`
      );
    };

    const queueResultsViewportHeight = () => {
      if (resultsLayoutFrame) window.cancelAnimationFrame(resultsLayoutFrame);
      resultsLayoutFrame = window.requestAnimationFrame(updateResultsViewportHeight);
    };

    const isMobileSearchViewport = () => window.matchMedia('(max-width: 719px)').matches;

    const resetAnimatedPlaceholder = () => {
      if (!inputWrap || !placeholderTrack) return;
      inputWrap.classList.remove('is-placeholder-overflowing');
      placeholderTrack.style.removeProperty('--kk-hsp-placeholder-shift');
    };

    const updateAnimatedPlaceholder = () => {
      placeholderFrame = 0;
      resetAnimatedPlaceholder();

      if (!input || !inputWrap || !placeholderTrack || !placeholderText) return;
      if (input.value.length || !isMobileSearchViewport()) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      const overflow = Math.ceil(placeholderText.scrollWidth - placeholderTrack.clientWidth);
      if (overflow <= 2) return;

      placeholderTrack.style.setProperty('--kk-hsp-placeholder-shift', `${-overflow}px`);
      inputWrap.classList.add('is-placeholder-overflowing');
    };

    const queueAnimatedPlaceholder = () => {
      if (placeholderFrame) window.cancelAnimationFrame(placeholderFrame);
      placeholderFrame = window.requestAnimationFrame(updateAnimatedPlaceholder);
    };

    const alignSearchToMobileViewport = (behavior = 'auto') => {
      mobileAlignmentFrame = 0;
      if (!form || !input || document.activeElement !== input || !isMobileSearchViewport()) return;

      const viewport = window.visualViewport;
      const viewportTop = viewport ? viewport.offsetTop : 0;
      const topGap = 12;
      const formTop = form.getBoundingClientRect().top;
      const scrollDelta = Math.round(formTop - viewportTop - topGap);

      if (Math.abs(scrollDelta) < 2) {
        queueResultsViewportHeight();
        return;
      }

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollBy({
        top: scrollDelta,
        left: 0,
        behavior: reduceMotion ? 'auto' : behavior
      });
      queueResultsViewportHeight();
    };

    const queueMobileSearchAlignment = (behavior = 'auto') => {
      if (mobileAlignmentFrame) window.cancelAnimationFrame(mobileAlignmentFrame);
      mobileAlignmentFrame = window.requestAnimationFrame(() => alignSearchToMobileViewport(behavior));
    };

    const onVisualViewportChange = () => {
      queueResultsViewportHeight();
      if (input && document.activeElement === input) queueMobileSearchAlignment('auto');
    };

    const closeResults = ({ clear = false } = {}) => {
      window.clearTimeout(debounceTimer);
      if (requestController) {
        requestController.abort();
        requestController = null;
      }
      if (countRequestController) {
        countRequestController.abort();
        countRequestController = null;
      }
      if (results) {
        results.hidden = true;
        results.style.removeProperty('--kk-hsp-predictive-available-height');
        if (clear) results.replaceChildren();
      }
      if (input) input.setAttribute('aria-expanded', 'false');
      root.classList.remove('is-search-open');
      setStatus('');
    };

    const openResults = () => {
      if (!results || !results.childElementCount) return;
      results.hidden = false;
      if (input) input.setAttribute('aria-expanded', 'true');
      root.classList.add('is-search-open');
      updateResultsViewportHeight();
      queueResultsViewportHeight();
    };

    const normalizeResponse = () => {
      if (!results) return;
      const footer = results.querySelector('[data-predictive-search-footer]');
      if (footer && !showViewAll) footer.remove();
      results.querySelectorAll('a[href="#"]').forEach((link) => link.removeAttribute('href'));
    };

    const updatePredictiveSearchCount = async (term, currentRequest) => {
      if (!results || !form || !showViewAll) return;

      const countLabel = results.querySelector('[data-kk-predictive-search-count-label]');
      if (!countLabel) return;

      const updateCountLabel = (count) => {
        const numericCount = Number.parseInt(count, 10);
        if (Number.isNaN(numericCount)) return;
        const displayCount = numericCount >= 1000 ? '1000+' : numericCount;

        const template = numericCount === 1
          ? countLabel.dataset.kkPredictiveSearchCountTemplateOne
          : countLabel.dataset.kkPredictiveSearchCountTemplateOther;

        if (!template) return;
        countLabel.textContent = template.replace(/\[\[count\]\]/g, displayCount);
      };

      updateCountLabel(countLabel.dataset.kkPredictiveSearchCountFallback);

      const searchAction = form.getAttribute('action');
      if (!searchAction) return;

      if (countRequestController) countRequestController.abort();
      const controller = new AbortController();
      countRequestController = controller;

      try {
        const countUrl = new URL(searchAction, window.location.origin);
        countUrl.searchParams.set('view', 'kk-count');
        countUrl.searchParams.set('type', 'product');
        countUrl.searchParams.set('q', term);
        const response = await fetch(countUrl.toString(), {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
          signal: controller.signal
        });

        if (!response.ok) throw new Error(`Search count returned ${response.status}`);

        const html = await response.text();
        if (currentRequest !== requestNumber || input.value.trim() !== term) return;

        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const countElement = parsed.querySelector('[data-kk-search-count]');
        if (!countElement) return;

        updateCountLabel(countElement.getAttribute('data-kk-search-count'));
      } catch (error) {
        if (error.name !== 'AbortError') {
          updateCountLabel(countLabel.dataset.kkPredictiveSearchCountFallback);
        }
      } finally {
        if (countRequestController === controller) countRequestController = null;
      }
    };

    const fetchResults = async (term) => {
      if (!predictiveEnabled || !predictiveUrl || !results || !term) return;

      if (requestController) requestController.abort();
      if (countRequestController) {
        countRequestController.abort();
        countRequestController = null;
      }
      requestController = new AbortController();
      const currentRequest = ++requestNumber;
      root.classList.add('is-search-loading');
      setStatus(loadingText);

      try {
        const requestUrl = new URL(predictiveUrl, window.location.origin);
        requestUrl.searchParams.set('q', term);
        requestUrl.searchParams.set('section_id', 'predictive-search');
        requestUrl.searchParams.set('resources[limit]', '8');
        requestUrl.searchParams.set('resources[limit_scope]', 'each');
        const response = await fetch(requestUrl.toString(), {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
          signal: requestController.signal
        });

        if (!response.ok) throw new Error(`Predictive search returned ${response.status}`);
        const html = await response.text();
        if (currentRequest !== requestNumber || input.value.trim() !== term) return;

        results.innerHTML = html;
        normalizeResponse();
        openResults();
        updatePredictiveSearchCount(term, currentRequest);
        setStatus('');
      } catch (error) {
        if (error.name === 'AbortError') return;
        closeResults({ clear: true });
        setStatus(errorText);
      } finally {
        if (currentRequest === requestNumber) {
          root.classList.remove('is-search-loading');
          requestController = null;
        }
      }
    };

    const queueSearch = () => {
      if (!input) return;
      const term = input.value.trim();
      setClearVisibility();
      window.clearTimeout(debounceTimer);

      if (!term) {
        closeResults({ clear: true });
        return;
      }

      if (!predictiveEnabled) return;
      debounceTimer = window.setTimeout(() => fetchResults(term), 250);
    };

    const onInputKeydown = (event) => {
      if (event.key === 'Escape') {
        closeResults();
        return;
      }

      if (event.key === 'ArrowDown' && results && !results.hidden) {
        const firstResult = results.querySelector('a[href], button:not([disabled])');
        if (firstResult) {
          event.preventDefault();
          firstResult.focus();
        }
      }
    };

    const onSearchKeydown = (event) => {
      if (event.key !== 'Escape' || !results || results.hidden) return;
      event.preventDefault();
      if (input) input.focus();
      closeResults();
    };

    const onDocumentPointerDown = (event) => {
      if (!root.contains(event.target)) closeResults();
    };

    const onFocusOut = (event) => {
      if (!event.relatedTarget) return;

      window.setTimeout(() => {
        if (!root.contains(document.activeElement)) closeResults();
      }, 0);
    };

    const onSubmit = (event) => {
      if (!input || input.value.trim()) return;
      event.preventDefault();
      closeResults({ clear: true });
      input.focus();
    };

    listen(input, 'input', () => {
      queueAnimatedPlaceholder();
      if (!composing) queueSearch();
    });
    listen(input, 'compositionstart', () => { composing = true; });
    listen(input, 'compositionend', () => {
      composing = false;
      queueSearch();
    });
    listen(input, 'keydown', onInputKeydown);
    listen(input, 'focus', () => {
      if (results && results.childElementCount) openResults();
      if (!isMobileSearchViewport()) return;

      queueMobileSearchAlignment('smooth');
      window.clearTimeout(mobileAlignmentTimer);
      mobileAlignmentTimer = window.setTimeout(() => {
        queueMobileSearchAlignment('auto');
      }, 360);
    });
    listen(clearButton, 'click', () => {
      input.value = '';
      setClearVisibility();
      queueAnimatedPlaceholder();
      closeResults({ clear: true });
      input.focus();
    });
    listen(form, 'submit', onSubmit);
    listen(search, 'keydown', onSearchKeydown);
    listen(search, 'focusout', onFocusOut);
    listen(document, 'pointerdown', onDocumentPointerDown, true);
    listen(window, 'resize', () => {
      queueResultsViewportHeight();
      queueAnimatedPlaceholder();
    }, { passive: true });
    listen(window, 'scroll', queueResultsViewportHeight, { passive: true });
    if (window.visualViewport) {
      listen(window.visualViewport, 'resize', onVisualViewportChange, { passive: true });
      listen(window.visualViewport, 'scroll', queueResultsViewportHeight, { passive: true });
    }

    cleanup.push(() => {
      window.clearTimeout(debounceTimer);
      window.clearTimeout(mobileAlignmentTimer);
      if (resultsLayoutFrame) window.cancelAnimationFrame(resultsLayoutFrame);
      if (mobileAlignmentFrame) window.cancelAnimationFrame(mobileAlignmentFrame);
      if (placeholderFrame) window.cancelAnimationFrame(placeholderFrame);
      if (requestController) requestController.abort();
      if (countRequestController) countRequestController.abort();
      resetAnimatedPlaceholder();
      root.classList.remove('is-search-open', 'is-search-loading');
    });

    setClearVisibility();
    queueAnimatedPlaceholder();
  };

  init(document.getElementById(rootId));

  const onSectionLoad = (event) => {
    if (!event.detail || event.detail.sectionId !== sectionId) return;
    init(event.target.querySelector('[data-kk-home-shop-paths]'));
  };

  const onSectionUnload = (event) => {
    if (!event.detail || event.detail.sectionId !== sectionId) return;
    const sectionRoot = event.target.querySelector('[data-kk-home-shop-paths]')
      || document.getElementById(rootId);
    destroy(sectionRoot);
    document.removeEventListener('shopify:section:load', onSectionLoad);
    document.removeEventListener('shopify:section:unload', onSectionUnload);
  };

  document.addEventListener('shopify:section:load', onSectionLoad);
  document.addEventListener('shopify:section:unload', onSectionUnload);
})();
